/**
 * HTTP Client
 *
 * Fetch-based client for the existing QR Phone API:
 * - Adds the Authorization bearer token.
 * - Decorates every request with the query params the API expects
 *   (request_from, language, appversion).
 * - Supports JSON and multipart (FormData) bodies.
 *
 * The access token DOES expire (it comes from the corporate access service), so
 * every authenticated request first makes sure the token still has a usable
 * margin of validity, and a 401 is retried once with a freshly renewed token
 * before the session is given up. This matters because this app is meant to be
 * left open all day waiting for calls. Only a genuinely dead session ends up in
 * the auth provider's checkError.
 */

import { API_CONFIG, API_REQUEST_SOURCE, APP_VERSION, getApiLanguageId } from '@/config/constants';
import { getToken } from './auth';
import { ensureValidToken } from '@/auth/sso';
import { IMPERSONATION_HEADER, getImpersonation, stopImpersonation } from './impersonation';

/**
 * HTTP request options
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
  /** Internal: marks the single retry allowed after renewing an expired token. */
  retriedAfterRenewal?: boolean;
}

/**
 * HTTP response wrapper
 */
interface HttpResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

/**
 * Custom HTTP Error class
 */
export class HttpError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Append the standard request decorations as query params
 */
const decorateUrl = (endpoint: string): string => {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_CONFIG.BASE_URL}${endpoint}`;
  const sep = url.includes('?') ? '&' : '?';
  const decorations = new URLSearchParams({
    request_from: API_REQUEST_SOURCE,
    language: String(getApiLanguageId()),
    appversion: APP_VERSION,
  });
  return `${url}${sep}${decorations.toString()}`;
};

/**
 * Make an HTTP request
 */
const request = async <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<HttpResponse<T>> => {
  const {
    body,
    skipAuth = false,
    headers: customHeaders,
    retriedAfterRenewal = false,
    ...restOptions
  } = options;

  const headers: Record<string, string> = {
    ...(customHeaders as Record<string, string>),
  };

  if (!skipAuth) {
    // Renew first when the token is close to expiring, so a request is never
    // sent with a token the API is about to reject.
    await ensureValidToken();
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Downward impersonation: the actor keeps their own token; the API swaps
  // the request's principal for this subject after validating the real role.
  const impersonation = skipAuth ? null : getImpersonation();
  if (impersonation) {
    headers[IMPERSONATION_HEADER] = impersonation.sub;
  }

  let requestBody: BodyInit | undefined;
  if (body instanceof FormData) {
    // Let the browser set the multipart boundary
    body.append('request_from', API_REQUEST_SOURCE);
    body.append('language', String(getApiLanguageId()));
    body.append('appversion', APP_VERSION);
    requestBody = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    requestBody = JSON.stringify({
      request_from: API_REQUEST_SOURCE,
      language: getApiLanguageId(),
      appversion: APP_VERSION,
      ...(body as Record<string, unknown>),
    });
  }

  const response = await fetch(decorateUrl(endpoint), {
    ...restOptions,
    headers,
    body: requestBody,
  });

  let data: T;
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    data = await response.json();
  } else {
    data = (await response.text()) as unknown as T;
  }

  if (!response.ok) {
    const payload = data as { message?: string; action?: string; code?: string } | undefined;

    // A single 401 usually means the token expired between the check above and
    // the request reaching the API (a suspended laptop, a throttled background
    // tab). Renew once and repeat before deciding the session is over: ending
    // it would drop an operator out of the app, possibly mid-call.
    if (response.status === 401 && !skipAuth && !retriedAfterRenewal) {
      const renewed = await ensureValidToken();
      if (renewed) {
        return request<T>(endpoint, { ...options, retriedAfterRenewal: true });
      }
    }

    // Failsafe: if the API rejects the impersonation itself (target revoked,
    // disabled, role raised…), drop the state and come back as the real user
    // instead of leaving every request failing.
    if (
      impersonation &&
      response.status === 403 &&
      String(payload?.code ?? '').startsWith('IMPERSONATION')
    ) {
      stopImpersonation();
      window.location.hash = '#/';
      window.location.reload();
    }

    // 4xx messages are localized and user-safe; 5xx bodies may leak internals
    const message =
      response.status >= 500
        ? 'ra.notification.http_error'
        : payload?.message || 'ra.notification.http_error';
    throw new HttpError(message, response.status, data);
  }

  return {
    data,
    status: response.status,
    ok: response.ok,
  };
};

/**
 * HTTP client with convenience methods
 */
export const httpClient = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default httpClient;
